import React from 'react'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { Divider, List, ListItemText, Popover, Theme, Tooltip, useMediaQuery } from '@mui/material'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import Avatar from '@mui/material/Avatar'
import makeStyles from '@mui/styles/makeStyles'
import StoreIcon from '@mui/icons-material/Store'
import { usePopover } from 'app/menu/components/TopBar/use-popover'
import { TopBarMenuItem } from './TopBarMenuItem'
import { useDispatch, useSelector } from 'react-redux'
import { ShopStatus } from '@cbgms/api'
import { useCompanyShops } from 'app/shops/hooks/use-company-shops'
import N from 'utils/navigation'
import { setCurrentShop } from 'app/auth/state/actions'
import { IRootReducerState } from 'state/rootReducer'
import { TopBarListItem } from './TopBarListItem'
import FullShop from 'core/utils/FullShop'
import { clearEcommerce } from 'utils/TagManager/tag-manager'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'
import { useFeature } from '@cbgms/base/feature-flags'
import classNames from 'classnames'

const useStyles = makeStyles(theme => ({
  shops: {
    minWidth: 260,
    maxWidth: 360
  },
  textOverflow: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  inline: {
    display: 'inline'
  },
  menuButtonText: {
    width: '80px',
    [theme.breakpoints.up('xl')]: {
      width: '120px'
    }
  },
  menuButtonTextPortal: {
    width: '100px',
    [theme.breakpoints.up('lg')]: {
      width: '150px'
    }
  }
}))

export const ShopsMenu: React.FC = () => {
  const classes = useStyles()
  const location = useLocation()
  const currentShopID = useSelector<IRootReducerState, string>(state => state.auth.currentShop)
  const dispatch = useDispatch()
  const isTopbarUXChangeForTimerEnabled = useFeature('topbar-ux-change-for-timer')
  const isSmallerThanXL = useMediaQuery((theme: Theme) => theme.breakpoints.down('xl'))
  const { data: allShops } = useCompanyShops()
  const shops = allShops?.filter(shop => shop.Status !== ShopStatus.Archive)

  const currentShop = shops?.filter(e => e.ID === currentShopID)[0]
  const [shopsEl, shopsOpen, handleShopsClick, handleShopsClose] = usePopover()

  const isPortal = useIsPortal()
  const hasB2BOrderPopup = useFeature('b2b-order-exit-popup')
  const match = useRouteMatch<{ workorderUUID: string }>('/orders/:workorderUUID')
  const workorderUUID = match?.params.workorderUUID

  const setCurrentShopAndRedirectToHome = async (ID: string) => {
    if (ID !== currentShopID) {
      // check if changing the shop from order page (based on feature flag)
      if (isPortal && workorderUUID && hasB2BOrderPopup) {
        N.push('/', { pendingShopChange: true, shopID: ID })
      } else {
        clearEcommerce()
        // TODO since it's complicated to stay on the page you were without 404s,
        //  we decided to navigate to the dashboard when switching shops.
        //  Here is a story to think this through and remove this default link => https://lkqeurope.atlassian.net/browse/GMS-6808
        if (location.pathname !== '/') {
          N.push('/', { plannedDurationSkip: true, shopID: ID })
        }
        dispatch(setCurrentShop(ID as string))
      }
    }
  }

  return (
    <>
      {isTopbarUXChangeForTimerEnabled && isSmallerThanXL ? (
        <TopBarMenuItem isMenu badge={0} onClick={handleShopsClick} startIcon={<StoreIcon />}>
          <Tooltip
            title={currentShop ? <FullShop shopID={currentShop.ID} shopName={currentShop.Name} shopCity={currentShop.VisitorCity} /> : ''}
          >
            <div
              className={
                isSmallerThanXL ? classNames(isPortal ? classes.menuButtonTextPortal : classes.menuButtonText, classes.textOverflow) : ''
              }
            >
              {currentShop && <FullShop shopID={currentShop.ID} shopName={currentShop.Name} shopCity={currentShop.VisitorCity} />}
            </div>
          </Tooltip>
        </TopBarMenuItem>
      ) : (
        <TopBarMenuItem isMenu badge={0} onClick={handleShopsClick} startIcon={<StoreIcon />}>
          {currentShop && <FullShop shopID={currentShop.ID} shopName={currentShop.Name} shopCity={currentShop.VisitorCity} />}
        </TopBarMenuItem>
      )}

      <Popover
        open={shopsOpen}
        anchorEl={shopsEl}
        onClose={handleShopsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <List className={classes.shops}>
          {shops &&
            shops.length > 0 &&
            shops.map(({ ID, Name, VisitorCity }, index, array) => (
              <React.Fragment key={index}>
                <TopBarListItem onClick={() => setCurrentShopAndRedirectToHome(ID)}>
                  <ListItemAvatar>
                    <Avatar alt={Name}>{Name[0]}</Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={Name} secondary={VisitorCity} />
                </TopBarListItem>
                {index + 1 !== array.length ? <Divider component='li' /> : null}
              </React.Fragment>
            ))}
        </List>
      </Popover>
    </>
  )
}
